import { Box, Button, Typography, Checkbox, FormControlLabel, CircularProgress, useMediaQuery } from "@mui/material";
import { useNavigate } from "react-router-dom";
import styles from "../../assets/styles/form.module.css";
import { useState } from "react";
import { PinnacleTextField } from "../../components/PinnacleTextField";
import getTheme from "../../theme/theme";
import { PinnacleThemes } from "../../theme/types";
import { login } from "../../api/services/auth";
import { ADMIN_STORAGE_KEY, E_ENTER_EMAIL_ADDRESS, E_ENTER_PASSWORD, LOGIN_SUCCESS_MESSAGE, PINNACLE_LOGO_URL } from "../../utils/constants";
import PasswordAdornment from "../../components/common/PasswordAdornment/PasswordAdornment";
import { NotificationTypes } from "../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider.types";
import { pushNotification } from "../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider";
import { handleApiError } from "../../utils/errorHandler";

export default function LoginPage() {
    const theme = getTheme(PinnacleThemes.BASE_THEME);
    const navigate = useNavigate();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));


    const [email, setEmail] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [formDisabled, setFormDisabled] = useState<boolean>(false);
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [isRemember, setIsRemember] = useState<boolean>(true);
    const [emailTouched, setEmailTouched] = useState<boolean>(false);
    const [passwordTouched, setPasswordTouched] = useState<boolean>(false);
    const [loginError, setLoginError] = useState<string | null>(null);

    const isEmailValid = email.length > 0; // EMAIL_REGEX.test(email);
    const isPasswordValid = password.length > 0;

    const handleTogglePassword = () => setShowPassword((prev) => !prev);

    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setEmail(e.target.value);
        if (!emailTouched) setEmailTouched(true);
    };

    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPassword(e.target.value);
        if (!passwordTouched) setPasswordTouched(true);
        if (loginError) setLoginError(null);
    };

    const handleRememberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setIsRemember(e.target.checked);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setEmailTouched(true);
        setPasswordTouched(true);

        // Early return if invalid, for clarity
        if (!isEmailValid || !isPasswordValid) {
            return;
        }

        setFormDisabled(true);

        try {
            // delete the key from local storage and session storage
            localStorage.removeItem(ADMIN_STORAGE_KEY);
            sessionStorage.removeItem(ADMIN_STORAGE_KEY);

            const loginResponse = await login({ email, password });
            const token = loginResponse.data?.token;
            const user = loginResponse.data?.user;

            if (!token || !user) {
                throw new Error("Invalid login response.");
            }

            if (isRemember) {
                localStorage.setItem(ADMIN_STORAGE_KEY, token);
            } else {
                sessionStorage.setItem(ADMIN_STORAGE_KEY, token);
            }

            pushNotification({
                title: LOGIN_SUCCESS_MESSAGE,
                type: NotificationTypes.Success,
            });

            navigate("/");
        } catch (error) {
            const { errorMessage } = handleApiError(error);
            setLoginError(errorMessage);
        } finally {
            setFormDisabled(false);
        }
    };

    return (
        <div className={styles["form-container"]}>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: isMobile ? "center" : "flex-end",
                    alignItems: isMobile ? "center" : "flex-start",
                    width: "100%",
                    padding: 4,
                    pr: isMobile ? 0 : 25,
                }}
            >
                <Box
                    sx={{
                        maxWidth: "450px",
                        minWidth: "300px",
                        width: "90%",
                        borderRadius: "5px",
                        boxSizing: "border-box",
                        padding: "35px 16px 16px",
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 3,
                    }}
                >
                    <img
                        src={PINNACLE_LOGO_URL}
                        alt='pinnacle-logo'
                        style={{
                            width: "270px",
                            height: "auto",
                            alignSelf: "center",
                        }}
                    />
                    <Typography variant="h3" gutterBottom>
                        Admin
                    </Typography>
                    <form onSubmit={handleSubmit} noValidate>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <PinnacleTextField
                                error={emailTouched && !isEmailValid}
                                color={emailTouched && (!isEmailValid || !!loginError) ? 'error' : 'primary'}
                                helperText={emailTouched && !isEmailValid ? E_ENTER_EMAIL_ADDRESS : ''}
                                id="email"
                                autoFocus
                                label="Email Address"
                                variant="outlined"
                                onChange={handleEmailChange}
                                onBlur={() => setEmailTouched(true)}
                                value={email}
                                autoComplete="email"
                                slotProps={{
                                    htmlInput: {
                                        sx: {
                                            'aria-label': 'email'
                                        }
                                    }
                                }}
                                disabled={formDisabled}
                            />
                            <PinnacleTextField
                                error={passwordTouched && (!isPasswordValid || !!loginError)}
                                color={passwordTouched && (!isPasswordValid || !!loginError) ? 'error' : 'primary'}
                                helperText={
                                    passwordTouched && !isPasswordValid
                                        ? E_ENTER_PASSWORD
                                        : loginError
                                }
                                id="password"
                                label="Password"
                                variant="outlined"
                                onChange={handlePasswordChange}
                                onBlur={() => setPasswordTouched(true)}
                                value={password}
                                type={showPassword ? 'text' : 'password'}
                                autoComplete="current-password"
                                disabled={formDisabled}
                                slotProps={{
                                    input: {
                                        endAdornment: <PasswordAdornment show={showPassword} toggleShow={handleTogglePassword} />,
                                    },
                                }}
                            />
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 2 }}>
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={isRemember}
                                            onChange={handleRememberChange}
                                            color="primary"
                                            size="small"
                                            sx={{
                                                color: "#003367",
                                                '&.Mui-checked': {
                                                    color: "#003367",
                                                },
                                            }}
                                        />
                                    }
                                    label="Remember Me"
                                />
                            </Box>
                        </Box>
                        <Box sx={{ mt: 2 }}>
                            <Button
                                fullWidth
                                variant="contained"
                                type="submit"
                                disabled={!isEmailValid || !isPasswordValid || formDisabled}
                                sx={{ mb: 1.5 }}
                            >
                                {formDisabled ? (
                                    <CircularProgress size={24} sx={{ color: "#90caf9" }} />
                                ) : (
                                    "SIGN IN"
                                )}
                            </Button>
                        </Box>
                    </form>
                </Box>
            </Box>
        </div>
    );
}